import { allModifiers } from "../../../shared/src/lib/modifiers";
import { keywords } from "../../../shared/src/lib/keywords";

const languageMap: { [key: string]: string } = {
  // GFM aliases -> Jadle language
  'html': 'markup',
  'xml': 'markup',
  'svg': 'markup',
  'react': 'jsx',
  'kt': 'kotlin',
  'objc': 'objectivec',
  'javascript': 'js-extras',
  'js': 'js-extras',
  're': 'reason',
  'rs': 'rust',
  'gql': 'graphql',
  'yml': 'yaml',
  'golang': 'go',
  'c++': 'cpp',
  'md': 'markdown',
  'py': 'python',
};

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

export function isMarkdown(text: string): boolean {
  if (!text) return false;

  // Check for Jadle-specific keywords and modifiers at the start of lines.
  // This is a strong signal that the text has been converted from Markdown.
  const jadleSigs = [...keywords, ...allModifiers];
  const jadlePattern = new RegExp(
    `^\\s*(${jadleSigs.map(escapeRegExp).join("|")})`,
    "m",
  );
  if (jadlePattern.test(text)) {
    return false;
  }

  // More robust check for various markdown patterns
  const patterns = [
    /^#{1,6}\s/m, // ATX Headers
    /^\s*([-*_])(\s*?\1){2,}\s*$/m, // Thematic breaks
    /^>\s/m, // BlockQuotes
    /^\s*`{3,}/m, // Fenced code blocks
    /\[[^\]]+\]\([^\)]+\)/, // Links
    /!\[[^\]]+\]\([^\)]+\)/, // Images
    /^\s*([-*+]|\d+\.)\s/m, // Lists
    /(\*\*|__|~~).+?\1/, // Strong emphasis/strikethrough
    /(\*|_)[^*_]+?\1/, // Emphasis
  ];
  return patterns.some((p) => p.test(text));
}

export default function markdownToJadle(markdown: string): string {
  const lines = markdown.split(/\r?\n/);
  const result: string[] = [];
  let inCode = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // 1. Fenced Code Blocks
    const fenceMatch = line.match(/^(\s*[`~]{3,})(.*)$/);
    if (fenceMatch) {
      if (!inCode) {
        // This is an opening fence
        inCode = true;
        const fence = fenceMatch[1];
        const infoString = fenceMatch[2].trim();

        if (infoString) {
          const parts = infoString.split(/\s+/);
          const lang = parts[0].toLowerCase();

          if (languageMap[lang]) {
            parts[0] = languageMap[lang];
            const newInfoString = parts.join(' ');
            result.push(`${fence}${newInfoString}`);
          } else {
            // Not in map, pass through as is. This covers Jadle-supported languages
            // and other unsupported languages.
            result.push(line);
          }
        } else {
          // No info string, just pass through
          result.push(line);
        }
      } else {
        // This is a closing fence
        inCode = false;
        result.push(line);
      }
      continue;
    }

    if (inCode) {
      result.push(line);
      continue;
    }

    // 2. Headings
    // ATX headings
    if (line.startsWith('#')) {
      if (line.startsWith('###### ')) {
        result.push(`subtitle: ${line.slice(7)}`);
        continue;
      }
      if (line.startsWith('##### ')) {
        result.push(`subtitle: ${line.slice(6)}`);
        continue;
      }
      if (line.startsWith('#### ')) {
        result.push(`subtitle: ${line.slice(5)}`);
        continue;
      }
      if (line.startsWith('### ')) {
        result.push(`subtitle: ${line.slice(4)}`);
        continue;
      }
      if (line.startsWith('## ')) {
        result.push(`title: ${line.slice(3)}`);
        continue;
      }
      if (line.startsWith('# ')) {
        result.push(`supertitle: ${line.slice(2)}`);
        continue;
      }
    }

    // Setext headings
    if (i > 0 && lines[i - 1].trim().length > 0) {
      if (line.match(/^={3,}\s*$/)) {
        const prevLine = result.pop();
        if (prevLine) {
          result.push(`supertitle: ${prevLine}`);
          continue;
        }
      }
      if (line.match(/^-{3,}\s*$/)) {
        const prevLine = result.pop();
        if (prevLine) {
          result.push(`title: ${prevLine}`);
          continue;
        }
      }
    }

    // 3. Thematic Break
    if (line.match(/^\s*([-*_])(\s*?\1){2,}\s*$/)) {
      result.push('line: 1');
      continue;
    }

    // 4. BlockQuotes
    if (line.startsWith('> ')) {
      const blockquoteContent = [];
      let j = i;
      // Consume all consecutive blockquote lines
      while (j < lines.length && lines[j].startsWith('> ')) {
        const content = lines[j].slice(2); // Get content after '> '
        if (content.trim()) { // Only add if there is content
          blockquoteContent.push(content.trim());
        }
        j++;
      }
      // Join the content into a single line
      if (blockquoteContent.length > 0) {
        result.push(`box: ${blockquoteContent.join(' ')}`);
      }
      // Advance the main loop counter to skip processed lines
      i = j - 1;
      continue;
    }

    // 5. Lists
    const listItemMatch = line.match(/^(\s*)([-*+]|\d+\.)\s+(.*)/);
    if (listItemMatch) {
      const [, indent, marker, content] = listItemMatch;
      const isOrdered = /^\d+\./.test(marker);
      const isIndented = indent.length > 0;

      if (isOrdered) {
        result.push(`${isIndented ? '##:' : '#:'} ${content}`);
      } else {
        result.push(`${isIndented ? '**:' : '*:'} ${content}`);
      }
      continue;
    }

    // 6. Inline transformations (on what's left)
    let processedLine = line;

    // Images: ![alt](src) -> image: src \n caption: alt
    processedLine = processedLine.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt, src) => {
      return `image: ${src}\n  caption: ${alt}`;
    });

    // Links: [text](url) -> {{text}} \n url: url
    processedLine = processedLine.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, (_m, txt, url) => {
      return `{{${txt}}}\n  url: ${url}`;
    });

    // Code spans `code` -> code (strip backticks)
    // This should be done before emphasis processing.
    processedLine = processedLine.replace(/`([^`]+)`/g, '$1');

    // Emphasis and Strong
    // Using a single pass with alternation to prevent incorrect nested replacements.
    // The order of alternation is important: from most specific (***) to least specific (*).

    // Using '*' for bold and italic
    processedLine = processedLine.replace(/\*{3}(.+?)\*{3}|\*{2}(.+?)\*{2}|\*(.+?)\*/g, (match, g1, g2, g3) => {
      if (g1) return `**''${g1}''**`; // ***foo*** -> **''foo''**
      if (g2) return `**${g2}**`;   // **foo** -> **foo**
      if (g3) return `''${g3}''`;     // *foo* -> ''foo''
      return match;
    });

    // Using '_' for underline and italic (with simplified "not-in-word" rule)
    processedLine = processedLine.replace(
      /(?<!\w)_{3}(.+?)_{3}(?!\w)|(?<!\w)_{2}(.+?)_{2}(?!\w)|(?<!\w)_(.+?)_(?!\w)/g,
      (match, g1, g2, g3) => {
          if (g1) return `''__${g1}__''`; // ___foo___ -> ''__foo__''
          if (g2) return `__${g2}__`;   // __foo__ -> __foo__
          if (g3) return `''${g3}''`;     // _foo_ -> ''foo''
          return match;
      }
    );

    // Hard line breaks (convert to space)
    processedLine = processedLine.replace(/\\$|\s{2,}$/, ' ');

    // Strip remaining HTML tags
    processedLine = processedLine.replace(/<[^>]*>/g, '');

    // 7. Paragraphs (if anything is left)
    if (processedLine.trim().length > 0) {
      // Avoid adding 'paragraph:' to lines that became multi-line Jadle (like images/links)
      if (processedLine.includes('\n')) {
        result.push(processedLine);
      } else {
        result.push(processedLine.trim());
      }
    } else if (line.trim().length === 0) {
      // Preserve blank lines
      result.push('');
    }
  }
  return result.join('\n');
}
